import React from 'react';
import { useLocation } from 'react-router-dom';
import { QuoteProvider } from '../context/QuoteContext';
import { QuoteWizard } from './QuoteWizard';
import { QuoteDraftGate } from './QuoteDraftGate';
import { QuoteLoadFailure } from './QuoteLoadFailure';
import { useNewQuoteReset } from '../hooks/useNewQuoteReset';
import { useQuoteLoader } from '../hooks/useQuoteLoader';
import { useMountLoadMode } from '../hooks/useMountLoadMode';
import { useResetOnForeignFreshOpen } from '../hooks/useResetOnForeignFreshOpen';
import { useQuoteStepOrder } from '../hooks/useQuoteStepOrder';
import { LoadingSpinner } from '@/shared/components/ui/loadingspinner';

export const QuoteJourney: React.FC = () => {
    // Must precede useQuoteLoader: a resumed quote hydrates onto the clean slice.
    const { sameJourney } = useNewQuoteReset();
    const mountLoadMode = useMountLoadMode(sameJourney);
    useResetOnForeignFreshOpen(mountLoadMode, sameJourney);
    const { key: navigationKey } = useLocation();

    // Load existing quote if quoteId is provided in the URL
    const { isLoadingQuote, loadError, loadedQuote, refetchQuote } = useQuoteLoader(mountLoadMode);
    // Epic 7.1.8 Slice 1 (P-D5/D9): gate on settled so the step order cannot
    // flip after the wizard has already rendered dealer-first for a frame.
    const { settled } = useQuoteStepOrder();

    // Terminal only until data exists: once loadedQuote is set, this is a
    // background refetch failure, not a wrong-quote render.
    if (loadError && !loadedQuote) {
        return <QuoteLoadFailure onRetry={refetchQuote} />;
    }

    if (isLoadingQuote) {
        return <LoadingSpinner message="Loading Quote..." className="min-h-screen p-4" size="xl" />;
    }

    if (!settled) {
        return (
            <LoadingSpinner message="Preparing quote journey..." className="min-h-screen p-4" size="xl" />
        );
    }

    return (
        // resetQuote() is Redux-only, and this subtree does not otherwise unmount
        // between journeys, so provider-local state would survive into a new quote.
        <QuoteProvider key={navigationKey} mountLoadMode={mountLoadMode}>
            <div className="min-h-screen bg-mp-background">
                <QuoteDraftGate>
                    <QuoteWizard />
                </QuoteDraftGate>
            </div>
        </QuoteProvider>
    );
};
